import { ProviderConfig, ChatSession, ChatMessage, StoryBible } from "./types";
import { estimateContextTokensForModel } from "./modelContextSizes";
import { DEFAULT_ANTHROPIC_MAX_TOKENS, getLiveContextTokens } from "./adapters";

const PROVIDERS_KEY = "wai.providers";
const SESSIONS_KEY = "wai.sessions";
const STORY_BIBLES_KEY = "wai.storyBibles";
const DEBUG_KEY = "wai.debug";
const CRITIQUE_PROMPT_KEY = "wai.critiquePrompt";

function readJson<T>(key: string, fallback: T): T {
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function genId(): string {
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Developer-only visibility into the hidden instructions this app sends to the AI but never
// shows in the chat itself (NOTE_DETECTION_INSTRUCTION, CRITIQUE_CHAPTER_PROMPT,
// REGENERATE_DISSATISFACTION_NOTE — see chatPrompts.ts) — needed because there's otherwise no
// way to remember or iterate on exact wording that's deliberately kept out of the visible UI.
// Deliberately no in-app toggle: enable from the browser console with
// `localStorage.setItem("wai.debug", "1")`, disable by removing it or setting any other value.
// This is a prompt-engineering tool for development, not something a public user should ever
// see or be tempted to switch on. When enabled, chat.ts logs the exact outgoing API request to
// the console right before every send/regenerate.
export function isDebugMode(): boolean {
  return localStorage.getItem(DEBUG_KEY) === "1";
}

// Raw persistence only — no knowledge of what the built-in default critique prompt text is or
// what a "minimum" fallback looks like, both of which are chatPrompts.ts's own concern
// (CRITIQUE_CHAPTER_PROMPT/MINIMUM_CRITIQUE_PROMPT/effectiveCritiquePrompt) so core/ doesn't
// import from the UI layer. `undefined` means "never customized, use the built-in default" —
// distinct from an explicit empty string, which settings.ts's Save handler never actually writes
// here (a blank field is coerced to MINIMUM_CRITIQUE_PROMPT before it reaches this function).
export function getCustomCritiquePrompt(): string | undefined {
  return localStorage.getItem(CRITIQUE_PROMPT_KEY) ?? undefined;
}

export function setCustomCritiquePrompt(value: string): void {
  localStorage.setItem(CRITIQUE_PROMPT_KEY, value);
}

export function getProviders(): ProviderConfig[] {
  return readJson<ProviderConfig[]>(PROVIDERS_KEY, []);
}

export function getProvider(id: string): ProviderConfig | undefined {
  return getProviders().find((p) => p.id === id);
}

export function saveProvider(provider: ProviderConfig): void {
  const providers = getProviders();
  const idx = providers.findIndex((p) => p.id === provider.id);
  if (idx >= 0) {
    providers[idx] = provider;
  } else {
    providers.push(provider);
  }
  writeJson(PROVIDERS_KEY, providers);
}

export function deleteProvider(id: string): void {
  writeJson(
    PROVIDERS_KEY,
    getProviders().filter((p) => p.id !== id)
  );
}

export function getSessions(): ChatSession[] {
  return readJson<ChatSession[]>(SESSIONS_KEY, []);
}

export function getSession(id: string): ChatSession | undefined {
  return getSessions().find((s) => s.id === id);
}

export function getOpenSessions(): ChatSession[] {
  return getSessions()
    .filter((s) => s.state === "open")
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export function getClosedSessions(): ChatSession[] {
  return getSessions()
    .filter((s) => s.state === "closed")
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

// All chats (open and closed) currently linked to a Story Bible — used by storyBible.ts's list
// card ("Linked to: ...") and Edit Story Bible screen. Includes closed ones deliberately, same
// scope deleteStoryBible's own cascade already operates on (it clears storyBibleId on every
// session, not just open ones) — callers that only want "currently in use" (the list card's
// compact line) filter to state === "open" themselves rather than this function assuming that
// for everyone.
export function getSessionsLinkedToStoryBible(storyBibleId: string): ChatSession[] {
  return getSessions().filter((s) => s.storyBibleId === storyBibleId);
}

export function saveSession(session: ChatSession): void {
  const sessions = getSessions();
  const idx = sessions.findIndex((s) => s.id === session.id);
  if (idx >= 0) {
    sessions[idx] = session;
  } else {
    sessions.push(session);
  }
  writeJson(SESSIONS_KEY, sessions);
}

// Closing hides a session from the active tab bar without losing any data — just a state
// flip, unlike deleteSession below which actually removes it from storage.
export function closeSession(id: string): void {
  const session = getSession(id);
  if (!session) return;
  session.state = "closed";
  session.updatedAt = Date.now();
  saveSession(session);
}

export function reopenSession(id: string): void {
  const session = getSession(id);
  if (!session) return;
  session.state = "open";
  session.updatedAt = Date.now();
  saveSession(session);
}

// Permanent removal from local storage — not a "deleted" state flag, an actual removal,
// since nothing needs to list/recover deleted sessions afterward.
export function deleteSession(id: string): void {
  writeJson(
    SESSIONS_KEY,
    getSessions().filter((s) => s.id !== id)
  );
}

// Unfiltered read, private — every exported reader below decides for itself whether deleted
// bibles belong in its result; writers (saveStoryBible et al.) always need the full array so a
// write never silently drops the other bucket (a naive `getStoryBibles()` re-save from a
// filtered list would have quietly purged every soft-deleted bible on the next unrelated save).
function getAllStoryBibles(): StoryBible[] {
  return readJson<StoryBible[]>(STORY_BIBLES_KEY, []);
}

export function getStoryBibles(): StoryBible[] {
  return getAllStoryBibles().filter((b) => !b.deletedAt);
}

// The History screen's "Deleted Story Bibles" section — see restoreStoryBible/purgeStoryBible.
export function getDeletedStoryBibles(): StoryBible[] {
  return getAllStoryBibles().filter((b) => !!b.deletedAt);
}

// Only ever needs to resolve an *active* bible in practice (its callers are all resolving a
// ChatSession.storyBibleId, and deleteStoryBible below clears that field on every session at the
// moment a bible is soft-deleted) — searching the filtered list is correct, not a shortcut.
export function getStoryBible(id: string): StoryBible | undefined {
  return getStoryBibles().find((b) => b.id === id);
}

export function saveStoryBible(bible: StoryBible): void {
  const bibles = getAllStoryBibles();
  const idx = bibles.findIndex((b) => b.id === bible.id);
  if (idx >= 0) {
    bibles[idx] = bible;
  } else {
    bibles.push(bible);
  }
  writeJson(STORY_BIBLES_KEY, bibles);
}

// Soft delete: sets deletedAt and unbinds every chat currently using this bible. Deliberately
// stronger than deleteProvider's dangling-reference-resolved-at-read-time pattern — a deleted
// bible should never keep silently steering an active chat's replies just because that chat's
// storyBibleId still points at it. Mirrors this app's existing "closing a chat is the soft
// delete" model (closeSession below); recoverable via restoreStoryBible, which deliberately does
// NOT restore any of the bindings this cleared — see that function's own comment.
export function deleteStoryBible(id: string): void {
  const bibles = getAllStoryBibles();
  const bible = bibles.find((b) => b.id === id);
  if (!bible) return;
  bible.deletedAt = Date.now();
  writeJson(STORY_BIBLES_KEY, bibles);

  const sessions = getSessions();
  let anySessionChanged = false;
  for (const session of sessions) {
    if (session.storyBibleId === id) {
      session.storyBibleId = undefined;
      anySessionChanged = true;
    }
  }
  if (anySessionChanged) writeJson(SESSIONS_KEY, sessions);
}

// Un-deletes a bible (clears deletedAt). Deliberately does not re-bind any chat that was unbound
// when it was deleted — recovering brings the bible itself back, not its old bindings; rebind a
// chat to it via Edit session if that's wanted, same as binding any other bible.
export function restoreStoryBible(id: string): void {
  const bibles = getAllStoryBibles();
  const bible = bibles.find((b) => b.id === id);
  if (!bible) return;
  bible.deletedAt = undefined;
  writeJson(STORY_BIBLES_KEY, bibles);
}

// Permanent removal — the "purge" half of the soft-delete/purge pair, only ever reachable from
// History's Deleted Story Bibles section. Mirrors deleteSession's own permanence for chats.
export function purgeStoryBible(id: string): void {
  writeJson(
    STORY_BIBLES_KEY,
    getAllStoryBibles().filter((b) => b.id !== id)
  );
}

// Rough token estimate: 2 characters per token. Real BPE tokenizers run closer to ~4
// chars/token for English and ~1.5-2 for Chinese — 2 is a deliberately conservative
// (over-)estimate across this app's expected mixed Chinese/English content, so this errs
// toward sending *less* than a model can actually take rather than risking an actual
// over-the-limit request. A real tokenizer (e.g. tiktoken) isn't used here: those are WASM- or
// large-table-based, more weight than this lightweight add-in's rough budget check needs.
const CHARS_PER_TOKEN_ESTIMATE = 2;

// Exported for storyBible.ts's editor screen, which shows this same estimate per entry and as a
// running total for the bible — reuses this one heuristic rather than a second copy.
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / CHARS_PER_TOKEN_ESTIMATE);
}

// Applied when a provider hasn't set its own maxContextTokens (see types.ts) — resolved per
// model rather than per provider/adapter type, since real context windows vary a lot even
// within one provider's current lineup (e.g. Qwen/Kimi both span roughly 30K-1M depending on
// which specific model you pick). Safe floor used when neither of the two more-specific tiers
// below applies — not an optimistic per-provider guess, but raised from the original 32,000 to
// 256,000 (2026-08-04) since that's now below every current-generation model's real minimum
// context size across all providers this app targets; 32K was trimming history far more
// aggressively than any of today's models actually require. Still a real cap, not "unlimited" —
// an unrecognized *older/smaller* model could still overflow at 256K, but that's judged less
// likely going forward than over-trimming a modern one.
const CONSERVATIVE_MAX_CONTEXT_TOKENS = 256000;

// Three-tier resolution:
// 1. `liveContextTokens` — the provider's own API already told us the real number for this
//    exact model (Gemini's inputTokenLimit, Anthropic's max_input_tokens — see the
//    ModelInfo.contextTokens each adapter's listModels() returns). Always accurate, nothing
//    to maintain, so this wins whenever it's available. Only ever available in the moment a
//    provider's model list is being actively fetched (the Add/Edit Provider form) — it's
//    never persisted, so callers without a live fetch in hand (the provider list card, actual
//    message-send truncation) fall through to tier 2 even for Gemini/Anthropic.
// 2. A name-pattern match against current popular models (core/modelContextSizes.ts) — covers
//    every provider, including Gemini/Anthropic for the no-live-data case above.
// 3. The conservative floor above.
// Set maxContextTokens explicitly on a provider to override all three with the exact number
// your chosen model supports.
export function getDefaultMaxContextTokens(
  provider: Pick<ProviderConfig, "adapterType" | "endpoint">,
  options?: { model?: string; liveContextTokens?: number }
): number {
  const live = resolveLiveContextTokens(provider, options);
  if (live !== undefined) {
    return live;
  }
  const estimated = options?.model
    ? estimateContextTokensForModel(provider.endpoint, options.model)
    : undefined;
  return estimated ?? CONSERVATIVE_MAX_CONTEXT_TOKENS;
}

// Shared by getDefaultMaxContextTokens/isModelSpecificMaxContext below — an explicit
// liveContextTokens (a caller that just fetched the model list itself, e.g. the model combobox)
// always wins outright; otherwise fall back to whatever the adapters' shared cache (adapters/
// types.ts's recordLiveContextTokens/getLiveContextTokens) already learned for this exact
// endpoint+model, if anything. **This is the fix for the real gap found 2026-08-12**:
// getTruncatedHistory's own call below never passed an explicit liveContextTokens, so tier 1
// (the live API number) was unreachable at actual send/truncation time even for a model whose
// real size was already fetched elsewhere (the model combobox) — every request silently used
// tier 2/3 instead. Fixing it here, rather than at getTruncatedHistory's call site, means every
// caller of these two functions benefits automatically, not just that one.
function resolveLiveContextTokens(
  provider: Pick<ProviderConfig, "adapterType" | "endpoint">,
  options?: { model?: string; liveContextTokens?: number }
): number | undefined {
  if (options?.liveContextTokens !== undefined) return options.liveContextTokens;
  return options?.model ? getLiveContextTokens(provider.endpoint, options.model) : undefined;
}

// Whether getDefaultMaxContextTokens' result for this same provider/model actually reflects
// that specific model (tier 1 or 2 above) rather than the generic CONSERVATIVE_MAX_CONTEXT_
// TOKENS floor (tier 3) — callers only need this to pick the right placeholder wording ("Model
// default: X" vs a plain "Default: X", since the floor isn't really *that model's* number, just
// a safe guess for an unrecognized one — see providerForm.ts/sessionSwitcher.ts's own
// placeholder logic). Mirrors the same two checks getDefaultMaxContextTokens itself makes,
// rather than changing that function's return shape for every other caller that only wants the
// number (getTruncatedHistory, the provider-switch clamp check, providerList.ts's card display).
export function isModelSpecificMaxContext(
  provider: Pick<ProviderConfig, "adapterType" | "endpoint">,
  options?: { model?: string; liveContextTokens?: number }
): boolean {
  if (resolveLiveContextTokens(provider, options) !== undefined) return true;
  return options?.model
    ? estimateContextTokensForModel(provider.endpoint, options.model) !== undefined
    : false;
}

// Two-tier resolution (simpler than getDefaultMaxContextTokens's three): a live number from the
// provider's own Models API when available (ModelInfo.maxOutputTokens — currently only Anthropic
// populates this, see adapters/anthropic.ts's listModels), else the flat DEFAULT_ANTHROPIC_MAX_
// TOKENS fallback. No name-pattern tier like context has — max reply length only matters for one
// adapter, and a live value is available whenever a model fetch has actually happened, so the
// "no live data" case is narrow (custom/unlisted model name, or never fetched) rather than the
// common case context's estimation tier exists for.
export function getDefaultMaxOutputTokens(options?: { liveMaxOutputTokens?: number }): number {
  return options?.liveMaxOutputTokens ?? DEFAULT_ANTHROPIC_MAX_TOKENS;
}

// Same idea as isModelSpecificMaxContext above, for the two-tier Max reply length resolution —
// true only when a live per-model number was actually available, false for the flat
// DEFAULT_ANTHROPIC_MAX_TOKENS fallback (not derived from any specific model).
export function isModelSpecificMaxOutput(options?: { liveMaxOutputTokens?: number }): boolean {
  return options?.liveMaxOutputTokens !== undefined;
}

// Reserved out of the budget so a full history window doesn't leave no room for the model's
// own reply.
const RESPONSE_HEADROOM_TOKENS = 2000;

/**
 * Token-aware history truncation: walks messages newest-to-oldest, accumulating an estimated
 * token cost, and keeps as many as fit under the provider's real
 * context budget — rather than a fixed message/exchange count. A short back-and-forth keeps
 * far more history than a fixed "last 15" would; a session with large "Use selected text"
 * attachments keeps proportionally less, rather than risking an oversized request. The full
 * session.messages array is always kept intact for display — this only affects what's resent.
 */
export function getTruncatedHistory(
  session: ChatSession,
  provider?: Pick<
    ProviderConfig,
    "maxContextTokens" | "systemPrompt" | "adapterType" | "endpoint" | "model"
  >
): { messages: ChatMessage[]; truncated: boolean; usedTokens: number } {
  const maxContextTokens =
    provider?.maxContextTokens ??
    getDefaultMaxContextTokens(provider ?? { adapterType: "openai-compatible", endpoint: "" }, {
      model: provider?.model,
    });
  const systemPromptTokens = provider?.systemPrompt ? estimateTokens(provider.systemPrompt) : 0;
  const budget = Math.max(maxContextTokens - RESPONSE_HEADROOM_TOKENS - systemPromptTokens, 1000);

  const included: ChatMessage[] = [];
  let used = 0;
  for (let i = session.messages.length - 1; i >= 0; i--) {
    const message = session.messages[i];
    // Estimate against whatever actually gets sent to the API (apiContent, when a message has
    // one — see ChatMessage.apiContent), not the shorter display-only content, so the budget
    // isn't silently underestimated for messages carrying extra API-only instructions.
    const cost = estimateTokens(message.apiContent ?? message.content);
    // Always keep at least the single most recent message even if it alone exceeds budget —
    // there's nothing better to send than the one message that prompted this request.
    if (used + cost > budget && included.length > 0) break;
    included.unshift(session.messages[i]);
    used += cost;
  }

  // Messages always strictly alternate user/assistant, and `included` is always a
  // contiguous suffix of session.messages — so whether the cutoff above lands on "user"
  // or "assistant" depends purely on whether it's an even or odd number of messages back
  // from the end, which the budget loop has no awareness of. A suffix starting on
  // "assistant" (its preceding user turn trimmed away) is invalid for at least Anthropic,
  // which hard-rejects any request whose first message isn't role "user". Drop that one
  // extra leading message if so — alternation guarantees the message right after it is
  // "user".
  if (included.length > 0 && included[0].role !== "user") {
    const droppedForAlternation = included.shift();
    if (droppedForAlternation) {
      used -= estimateTokens(droppedForAlternation.apiContent ?? droppedForAlternation.content);
    }
  }

  return {
    messages: included,
    truncated: included.length < session.messages.length,
    // The real current size of this chat's context right now — every message actually about to
    // be resent, plus the system prompt (Story Bible facts already folded in by the time this is
    // called — see chat.ts's effectiveProviderConfig). Not the ceiling it's budgeted against
    // (that's maxContextTokens above, never returned — callers that want it call
    // getDefaultMaxContextTokens themselves). Grows as the conversation continues; can shrink
    // (deleting a message pair, or a provider/session change that now truncates more of the
    // history away). Previously computed here and discarded — surfacing it was a documented
    // next step (CLAUDE.md's "Max-context-tokens" note) for a real usage display, not new math.
    usedTokens: used + systemPromptTokens,
  };
}
